import { create } from 'zustand'
import type { CallStatus, WireEvent } from './lib/session'

/**
 * Состояние живого разговора.
 *
 * Здесь Zustand на своём месте: это не данные сервера, а то, что
 * происходит прямо сейчас и меняется десятки раз в секунду. React Query
 * такому не хозяин — кэшировать и признавать устаревшим тут нечего.
 */

export interface LiveTurn {
  id: number
  role: 'agent' | 'human'
  text: string
  /** Что собеседник успел услышать до перебивания. */
  heard: string
  interrupted: boolean
  /** Задержка ответа, миллисекунды. Ноль — ещё не посчитана. */
  replyMs: number
  /** Реплика ещё пишется: текст появляется по мере генерации. */
  streaming: boolean
  at: number
}

/** Кто сейчас говорит. Показывается крупно, поэтому отдельное поле. */
export type Speaker = 'none' | 'agent' | 'human' | 'thinking'

interface CallState {
  status: CallStatus
  detail: string
  speaker: Speaker
  turns: LiveTurn[]
  micLevel: number
  agentLevel: number
  startedAt: number | null
  /** Идентификатор звонка в кабинете — по нему потом откроется отчёт. */
  callId: number | null

  begin: (callId: number) => void
  setStatus: (status: CallStatus, detail?: string) => void
  setLevel: (source: 'mic' | 'agent', level: number) => void
  handle: (event: WireEvent) => void
  reset: () => void
}

const initial = {
  status: 'idle' as CallStatus,
  detail: '',
  speaker: 'none' as Speaker,
  turns: [] as LiveTurn[],
  micLevel: 0,
  agentLevel: 0,
  startedAt: null as number | null,
  callId: null as number | null,
}

let nextTurnId = 1

export const useCallStore = create<CallState>((set, get) => ({
  ...initial,

  begin: (callId) => set({ ...initial, callId, status: 'connecting' }),

  setStatus: (status, detail = '') =>
    set((state) => ({
      status,
      detail,
      startedAt: status === 'live' && !state.startedAt ? Date.now() : state.startedAt,
      speaker: status === 'live' ? state.speaker : 'none',
    })),

  setLevel: (source, level) =>
    set(source === 'mic' ? { micLevel: level } : { agentLevel: level }),

  handle: (event) => {
    switch (event.type) {
      case 'user_speaking':
        set({ speaker: 'human' })
        break

      case 'user_turn':
        // Распознанная реплика человека приходит целиком: whisper отдаёт
        // текст, когда фраза закончилась.
        if (event.text) {
          set((state) => ({
            turns: [...state.turns, turn('human', event.text ?? '', event.at)],
            speaker: 'thinking',
          }))
        }
        break

      case 'response_started':
        // Заводим пустую реплику сразу: в неё будет по кусочкам
        // прилетать текст, и человек видит, что агент уже отвечает, а
        // не завис.
        set((state) => ({
          turns: [...state.turns, { ...turn('agent', '', event.at), streaming: true }],
          speaker: 'thinking',
        }))
        break

      case 'llm_delta':
        set((state) => ({ turns: appendToLast(state.turns, event.text ?? '') }))
        break

      case 'response_speaking':
        set({ speaker: 'agent' })
        break

      case 'response_finished':
        set((state) => ({
          turns: finishLast(state.turns, {
            text: event.text ?? '',
            replyMs: event.timings?.endToFirstAudio ?? 0,
          }),
          speaker: 'none',
        }))
        break

      case 'response_cancelled':
        // Перебивание. Услышанная часть сохраняется отдельно — на
        // странице видно, где именно человек вклинился.
        set((state) => ({
          turns: finishLast(state.turns, {
            text: event.text ?? '',
            heard: event.heard ?? '',
            interrupted: true,
            replyMs: event.timings?.endToFirstAudio ?? 0,
          }),
          speaker: 'human',
        }))
        break

      case 'provider_error':
        set({ status: 'error', detail: event.error ?? 'Ошибка провайдера' })
        break

      case 'call_ended':
        set({ speaker: 'none' })
        break
    }

    // Реплик может накопиться много, но обрезать их нельзя: это и есть
    // разговор, ради которого всё затевалось.
    void get
  },

  reset: () => set(initial),
}))

function turn(role: 'agent' | 'human', text: string, at: number): LiveTurn {
  return {
    id: nextTurnId++,
    role,
    text,
    heard: '',
    interrupted: false,
    replyMs: 0,
    streaming: false,
    at,
  }
}

/** Дописывает кусок текста в последнюю реплику агента. */
function appendToLast(turns: LiveTurn[], chunk: string): LiveTurn[] {
  if (!chunk) return turns

  const last = turns[turns.length - 1]
  if (!last || last.role !== 'agent' || !last.streaming) return turns

  return [...turns.slice(0, -1), { ...last, text: last.text + chunk }]
}

/**
 * Закрывает последнюю реплику агента.
 *
 * Текст берётся из события целиком, а не остаётся собранным из кусков:
 * при перебивании ядро знает, что реально прозвучало, а поток кусков —
 * нет, он успевает уехать вперёд.
 */
function finishLast(turns: LiveTurn[], patch: Partial<LiveTurn>): LiveTurn[] {
  const last = turns[turns.length - 1]
  if (!last || last.role !== 'agent') return turns

  return [...turns.slice(0, -1), { ...last, ...patch, streaming: false }]
}
